import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { ScrollElement } from '@prisjakt/ui-scroll';
import AdContainer from '../../../../components/ads';
import ErrorBoundary from '../../../../components/error-boundary';
import PreReleaseInformation from '../pre-release-information';
import PriceHistory from '../price-history';
import PriceList from '../price-list';
import ProductPriceHistory from '../../../product-price-history';
import ReadableProductInformation from '../readable-product-information';
import Recommendations from '../../../../components/recommendations';
import UserReviews from '../user-reviews';
import ReviewSummary from '../../../../components/review-summary';
import { PRODUCT_CONTEXT_ID } from '../../constants';
import { Maybe, Product, UserReviewSummary } from '../../../../types';
import { CurrentCampaign, RouteSection } from '../../types';
import {
  ModelType,
  ModelTypeEnum,
} from '../../../../components/recommendations/types';
import * as S from '../../styles';
import ProductTabMain from './product-tab-main';
import ProductTabProperties from './product-tab-properties';
import ProductTabReviews from './product-tab-reviews';

const ProductTabs: React.FC<IProductPageTabsProps> = props => {
  const {
    routeSection,
    isPreRelease,
    product,
    similarProducts,
    importantDates,
    fetchMoreData,
    getRecommendationsLink,
    userReviews,
    currentCampaign,
    userReviewsSummary,
    productConditionVisible,
  } = props;

  const { linkTo } = useContext(AppContext);

  if (!product) {
    return null;
  }

  return props.shouldRenderAllTabs ? (
    <>
      <S.TabContent id="main">
        <S.HideableContent visible={routeSection === 'main'}>
          {isPreRelease && product.releaseDate && (
            <PreReleaseInformation
              product={product}
              releaseDate={product.releaseDate}
            />
          )}
          {product.statistics &&
            product.statistics.pageInfo &&
            product.statistics.pageInfo.lowestPrice &&
            isPreRelease && (
              <PriceHistory
                route={linkTo('productPriceHistory', product)}
                productName={product.name}
                {...product.statistics}
              />
            )}
          {isPreRelease && similarProducts?.recommendations?.length ? (
            <Recommendations
              link={getRecommendationsLink(ModelTypeEnum.SIMILAR_PRODUCTS)}
              recommendations={similarProducts.recommendations}
              type={ModelTypeEnum.SIMILAR_PRODUCTS}
              contextId={PRODUCT_CONTEXT_ID}
            />
          ) : null}
          {product.prices && (
            <ScrollElement name="Pricelist" data-test="MainTabContent">
              <PriceList
                {...product}
                prices={product.prices}
                adOffset={1}
                campaign={currentCampaign as CurrentCampaign}
                variantGroups={product.variantGroups}
                productConditionVisible={productConditionVisible}
              />
            </ScrollElement>
          )}
          <ReadableProductInformation product={product} />
        </S.HideableContent>
      </S.TabContent>
      <S.TabContent id="properties">
        <S.HideableContent visible={routeSection === 'properties'}>
          <ErrorBoundary onError={returnNull}>
            <ProductTabProperties product={product} />
          </ErrorBoundary>
        </S.HideableContent>
      </S.TabContent>
      <S.TabContent id="reviews">
        <S.HideableContent visible={routeSection === 'reviews'}>
          <ErrorBoundary onError={returnNull}>
            <ScrollElement name="Reviews" data-test="ReviewsTabContent">
              <AdContainer
                placement="panorama_extra"
                format="panorama_1_wde"
                min={861}
                max={10000}
                scalable
                scaleBase=".pj-ui-panel--row"
                spacing={{
                  top: '10px',
                  bottom: '10px',
                }}
              />

              <AdContainer
                placement="dubbelpanorama1"
                format="panorama_1"
                min={0}
                max={860}
                spacing={{
                  top: '10px',
                  bottom: '10px',
                }}
              />

              <S.ReviewsWrapper>
                <S.Reviews>
                  <UserReviews
                    belongsTo={{
                      id: product.id,
                      name: product.name,
                      type: 'product',
                    }}
                    myReview={userReviews && userReviews.myReview}
                    nodes={userReviews && userReviews.nodes}
                    pageInfo={userReviews && userReviews.pageInfo}
                    showAds={false}
                  />
                </S.Reviews>

                <S.ReviewsSidebar>
                  <ReviewSummary
                    rating={userReviewsSummary.rating}
                    count={userReviewsSummary.count}
                    countTotal={userReviewsSummary.countTotal}
                    distribution={userReviewsSummary.distribution}
                    myReview={userReviews?.myReview}
                    belongsTo={{
                      id: product.id,
                      name: product.name,
                      type: 'product',
                    }}
                    showPrompt
                  />
                </S.ReviewsSidebar>
              </S.ReviewsWrapper>
            </ScrollElement>
          </ErrorBoundary>
        </S.HideableContent>
      </S.TabContent>
      <S.TabContent id="statistics">
        <S.HideableContent visible={routeSection === 'statistics'}>
          <ErrorBoundary onError={returnNull}>
            <ScrollElement
              name="Product Information"
              data-test="StatisticsTabContent"
            >
              <ProductPriceHistory
                data={{ product, importantDates }}
                fetchMoreData={fetchMoreData}
              />
            </ScrollElement>
          </ErrorBoundary>
        </S.HideableContent>
      </S.TabContent>
    </>
  ) : (
    <>
      <S.TabContent id="main">
        {routeSection === 'main' && (
          <ProductTabMain
            isPreRelease={isPreRelease}
            product={product}
            similarProducts={similarProducts}
            getRecommendationsLink={getRecommendationsLink}
            currentCampaign={currentCampaign}
            productConditionVisible={productConditionVisible}
          />
        )}
      </S.TabContent>

      <S.TabContent id="properties">
        {routeSection === 'properties' && (
          <ErrorBoundary onError={returnNull}>
            <ProductTabProperties product={product} />
          </ErrorBoundary>
        )}
      </S.TabContent>

      <S.TabContent id="reviews">
        {routeSection === 'reviews' && (
          <ErrorBoundary onError={returnNull}>
            <ProductTabReviews
              product={product}
              userReviews={userReviews}
              userReviewsSummary={userReviewsSummary}
            />
          </ErrorBoundary>
        )}
      </S.TabContent>

      <S.TabContent id="statistics">
        {routeSection === 'statistics' && (
          <ErrorBoundary onError={returnNull}>
            <ScrollElement
              name="Product Information"
              data-test="StatisticsTabContent"
            >
              <ProductPriceHistory
                data={{ product, importantDates }}
                fetchMoreData={fetchMoreData}
              />
            </ScrollElement>
          </ErrorBoundary>
        )}
      </S.TabContent>
    </>
  );
};

const returnNull = () => null;

interface IProductPageTabsProps {
  shouldRenderAllTabs: boolean;
  routeSection: RouteSection;
  isPreRelease: boolean | '' | null;
  product: Product;
  similarProducts: any;
  importantDates: Maybe<
    Maybe<{
      __typename?: 'ImportantDate' | undefined;
      startDate: string;
      endDate: string;
      name: Maybe<string>;
    }>[]
  >;
  fetchMoreData: () => void;
  getRecommendationsLink: (
    modelType: ModelType
  ) =>
    | {
        title: string;
        url: string;
        onClick: () => void | null;
      }
    | {
        title: string;
        url: string;
        onClick?: undefined;
      }
    | null;
  userReviews: any;
  currentCampaign: any;
  userReviewsSummary: UserReviewSummary;
  productConditionVisible: boolean;
}

export default ProductTabs;
